// Payment.js - Complete component for handling subscription payments

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderFour from "../../common/header/HeaderFour";
import FooterOne from "../../common/footer/FooterOne";
import StripeWrapper from "./../StripeWrapper";
import CLIFLoader from "../../elements/loader/ClifLoader";
import axios from "axios";
import API_BASE_URL from "../../common/config";
import API_URL_dash from "../../common/config2";
import {
  SuccessNotification,
  ErrorNotification,
} from "../../tosterNotification/tosters";
import SEO from "../../common/SEO";
import ReactGA from "react-ga4";

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [planDetails, setPlanDetails] = useState(null);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    // Track page view
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname,
      title: "Payment Page",
    });

    const verifyPaymentSession = async () => {
      try {
        setLoading(true);
        console.log("location", location.state);
        // First check if we have payment state passed directly from registration
        if (
          location.state &&
          location.state.planName &&
          location.state.userId
        ) {
          setUserId(location.state.userId);
          setPlanDetails({
            name: location.state.planName,
            price: location.state.planPrice,
            duration: location.state.planDuration,
            priceId: location.state.stripePriceId,
            customerName: location.state.userName,
            customerEmail: location.state.userEmail,
          });

          setLoading(false);
          return;
        }

        // Check if we have a pending payment stored in localStorage (recovery flow)
        const pendingPayment = localStorage.getItem("pendingPayment");
        if (pendingPayment) {
          const paymentData = JSON.parse(pendingPayment);
          setUserId(paymentData.userId);
          setPlanDetails({
            name: paymentData.planName,
            price: paymentData.planPrice,
            duration: paymentData.planDuration,
            priceId: paymentData.stripePriceId,
            customerName: paymentData.userName,
            customerEmail: paymentData.userEmail,
          });

          setLoading(false);
          return;
        }

        // If we don't have direct plan data, try to fetch from user profile
        const user = localStorage.getItem("user");
        const token = localStorage.getItem("token");

        // if (!user || !token) {
        //   // No user session found, redirect to pricing
        //   setError("No subscription selected. Please choose a plan first.");
        //   navigate("/subscription");
        //   return;
        // }

        // Parse user data
        const userData = JSON.parse(user);
        setUserId(userData._id);

        // Check if the user already has an active subscription
        try {
          const response = await axios.post(
            `${API_BASE_URL}/payment/subscription-status`,
            { userId: userData._id }
          );

          console.log("$$response", response.data);

          if (response.data.success && response.data.data.isActive) {
            // User already has an active subscription
            SuccessNotification("You already have an active subscription!");
            // Redirect to dashboard
            // navigate("/dashboard");
            return;
          }

          // If no subscription info in response or location state, redirect to pricing
          if (!location.state && !response.data.pendingPlan) {
            setError("Please select a subscription plan");
            navigate("/subscription");
            return;
          }

          // Use pending plan from API if available
          if (response.data.pendingPlan) {
            setPlanDetails({
              name: response.data.pendingPlan.name,
              price: response.data.pendingPlan.price,
              duration: response.data.pendingPlan.duration,
              priceId: response.data.pendingPlan.priceId,
              customerName: `${userData.firstname} ${userData.lastname}`,
              customerEmail: userData.email,
            });
          }
        } catch (error) {
          console.error("Error checking subscription status:", error);
          setError("Error verifying subscription status. Please try again.");
        }

        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    };

    verifyPaymentSession();
  }, [location, navigate]);

  const handlePaymentSuccess = async (paymentDetails) => {
    try {
      setLoading(true);

      // Send confirmation to server to activate subscription
      const response = await axios.post(
        `${API_BASE_URL}/payment/confirm-subscription`,
        {
          userId: userId,
          paymentId: paymentDetails.paymentId,
          planName: paymentDetails.plan,
          priceId: paymentDetails.priceId,
          amount: paymentDetails.amount,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        console.log("success", response.data);
        // Clear pending payment data
        localStorage.removeItem("pendingPayment");

        // Track successful payment
        ReactGA.event({
          category: "Payment",
          action: "Payment Success",
          label: paymentDetails.plan,
          value: Math.round(
            parseFloat(paymentDetails.amount.replace(/[^0-9.]/g, ""))
          ),
        });

        SuccessNotification(
          "Payment processed successfully! Your subscription is now active.",
          5000
        );

        // Update user data with subscription info
        const userData = JSON.parse(localStorage.getItem("user"));
        const updatedUserData = {
          ...userData,
          subscription: {
            plan: paymentDetails.plan,
            price: paymentDetails.amount,
            active: true,
            paymentId: paymentDetails.paymentId,
          },
        };
        localStorage.setItem("user", JSON.stringify(updatedUserData));

        // Redirect to dashboard
        const encodedUserData = encodeURIComponent(
          JSON.stringify(updatedUserData)
        );
        const userId = updatedUserData._id;
        const token = localStorage.getItem("token")
          ? encodeURIComponent(localStorage.getItem("token"))
          : "";
        const refreshToken = localStorage.getItem("refreshToken")
          ? encodeURIComponent(localStorage.getItem("refreshToken"))
          : "";

        // Redirect to dashboard with all data
        window.location.href = `${API_URL_dash}/dashboard?userData=${encodedUserData}&userId=${userId}&token=${token}&refreshToken=${refreshToken}`;
      } else {
        // Payment confirmation failed on server
        console.log("error", response.data);
        ErrorNotification(
          response.data.message ||
            "Failed to activate subscription. Please contact support."
        );
      }
    } catch (error) {
      console.error("Error confirming subscription:", error);
      ErrorNotification(
        "An error occurred while activating your subscription. Your payment was successful, but please contact support to ensure your account is updated."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentCancel = () => {
    // Return to previous page or pricing
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/subscription");
    }
  };

  if (loading) {
    return <CLIFLoader />;
  }

  if (error) {
    return (
      <>
        <SEO />
        <main className="main-wrapper">
          <HeaderFour />
          <div className="section section-padding">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-8">
                  <div className="error-message text-center">
                    <h3>Subscription Error</h3>
                    <p>{error}</p>
                    <button
                      className="btn btn-primary mt-4"
                      onClick={() => navigate("/subscription")}
                    >
                      View Available Plans
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FooterOne />
        </main>
      </>
    );
  }

  return (
    <>
      <SEO title="Complete Your Subscription Payment" />
      <main className="main-wrapper">
        <HeaderFour />
        <div className="section section-padding mt-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="payment-container card shadow-lg mt-5">
                  <div className="card-body">
                    <h3 className="text-center mb-4">
                      Complete Your Subscription
                    </h3>

                    {planDetails ? (
                      <div className="subscription-summary mb-4">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="plan-info">
                              <h4>{planDetails.name}</h4>
                              <div className="plan-details">
                                <div className="detail-item">
                                  <span className="label">Price:</span>
                                  <span className="value">
                                    {planDetails.price}
                                  </span>
                                </div>
                                <div className="detail-item">
                                  <span className="label">Duration:</span>
                                  <span className="value">
                                    {planDetails.duration}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="payment-info">
                              <h5>What's included:</h5>
                              <ul className="feature-list">
                                {planDetails.name === "STANDARD PLAN" && (
                                  <>
                                    <li>
                                      <i className="fas fa-check-circle"></i>{" "}
                                      Unlimited Resume Analysis
                                    </li>
                                    <li>
                                      <i className="fas fa-check-circle"></i>{" "}
                                      Advanced Job Matching
                                    </li>
                                    <li>
                                      <i className="fas fa-check-circle"></i>{" "}
                                      Full AI Suggestions
                                    </li>
                                    <li>
                                      <i className="fas fa-check-circle"></i>{" "}
                                      Interview Preparation
                                    </li>
                                  </>
                                )}
                                {planDetails.name === "PREMIUM PLAN" && (
                                  <>
                                    <li>
                                      <i className="fas fa-check-circle"></i>{" "}
                                      All Standard Features
                                    </li>
                                    <li>
                                      <i className="fas fa-check-circle"></i>{" "}
                                      Priority Support
                                    </li>
                                    <li>
                                      <i className="fas fa-check-circle"></i>{" "}
                                      Career Coaching
                                    </li>
                                    <li>
                                      <i className="fas fa-check-circle"></i>{" "}
                                      Employer Connections
                                    </li>
                                    <li>
                                      <i className="fas fa-check-circle"></i>{" "}
                                      Lifetime Updates
                                    </li>
                                  </>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center mb-4">
                        <p>
                          No plan details available. Please select a plan first.
                        </p>
                        <button
                          className="btn btn-primary"
                          onClick={() => navigate("/subscription")}
                        >
                          Choose a Plan
                        </button>
                      </div>
                    )}

                    {planDetails && (
                      <div className="payment-form-wrapper">
                        <StripeWrapper
                          planDetails={planDetails}
                          onSuccess={handlePaymentSuccess}
                          onCancel={handlePaymentCancel}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <FooterOne />
      </main>
      <style jsx="true">{`
        .payment-container {
          border-radius: 12px;
          overflow: hidden;
          border: none;
        }

        .subscription-summary {
          background-color: #f8f9fa;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 25px;
        }

        .plan-info h4 {
          color: #6600cc;
          margin-bottom: 15px;
          font-weight: 600;
        }

        .plan-details {
          margin-bottom: 15px;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 16px;
        }

        .label {
          color: #666;
          font-weight: 500;
        }

        .value {
          font-weight: 600;
          color: #333;
        }

        .payment-info h5 {
          margin-bottom: 12px;
          font-weight: 600;
        }

        .feature-list {
          list-style: none;
          padding-left: 0;
        }

        .feature-list li {
          margin-bottom: 8px;
          display: flex;
          align-items: flex-start;
        }

        .feature-list li i {
          color: #28a745;
          margin-right: 8px;
          font-size: 16px;
          margin-top: 4px;
        }

        .payment-form-wrapper {
          margin-top: 30px;
          border-top: 1px solid #e5e5e5;
          padding-top: 30px;
        }

        @media (max-width: 768px) {
          .subscription-summary .row {
            flex-direction: column;
          }

          .col-md-6 {
            margin-bottom: 20px;
          }
        }

        .btn-primary {
          background: linear-gradient(135deg, #7928ca, #6600cc);
          border: none;
          box-shadow: 0 4px 12px rgba(102, 0, 204, 0.3);
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 500;
          letter-spacing: 0.5px;
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          background: linear-gradient(135deg, #8a3cd0, #7211d8);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 0, 204, 0.35);
        }

        .error-message {
          background-color: #f8f9fa;
          border-radius: 8px;
          padding: 30px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .error-message h3 {
          color: #dc3545;
          margin-bottom: 15px;
        }

        .error-message p {
          font-size: 16px;
          margin-bottom: 20px;
        }
      `}</style>
    </>
  );
};

export default Payment;
